import axios from 'axios'
import Vue from 'vue'
import i18n from '../../i18n/index'
import router from '../../router'

// Защита от одновременного двойного открытия (быстрый двойной клик)
let openingMiniApp = false

// Обычная функция для получения названия продукта
const getProductName = (name) => {
  const productNames = {
    inventory: i18n.t('учетная_система_товары_и_услуги_in_qoute'),
    loyalty: i18n.t('программа_лояльности_in_quote'),
  }
  return productNames[name]
}

const miniapp = {
  namespaced: true,

  state: {
    visible: false,
    app: {
      name: null,
      url: null,
      params: {},
    },
  },

  mutations: {
    set(state, { type, value }) {
      state[type] = value
    },
  },

  actions: {
    // Открыть миниапп (guard от повторного входа при двойном диспатче)
    async open({ dispatch }, payload) {
      if (openingMiniApp) {
        return
      }
      openingMiniApp = true
      try {
        await dispatch('processOpen', payload)
      } finally {
        openingMiniApp = false
      }
    },

    async processOpen({ dispatch, rootState }, { name: rawName, params = {} }) {
      // Имя может прийти с пробелами из deeplink/баннера ("ofd ") — нормализуем
      const name = typeof rawName === 'string' ? rawName.trim() : rawName

      dispatch('tools/setGlobalLoading', true, { root: true })

      // Базовые конфигурации для всех мини-приложений
      // Список миниаппов формируется автоматически из env-переменных вида VITE_MINIAPP_<NAME>_URL
      const baseApps = Object.keys(import.meta.env)
        .map(key => key.match(/^VITE_MINIAPP_([A-Z0-9]+)_URL$/))
        .filter(Boolean)
        .map(match => match[1].toLowerCase())
      const miniAppConfig = {}

      // Создаем базовые конфигурации
      baseApps.forEach(app => {
        miniAppConfig[app] = {
          url: import.meta.env[`VITE_MINIAPP_${app.toUpperCase()}_URL`],
          name: import.meta.env[`VITE_MINIAPP_${app.toUpperCase()}_NAME`],
        }
      })

      // Специальные конфигурации с дополнительными свойствами
      const cashRegisterItemListNewCount = rootState.cashRegisters.cashRegisterItemList.filter(item => item.cashRegister.status === 'NEW').length

      // Дополняем ofd дополнительными свойствами
      miniAppConfig.ofd.before = () => {
        if (cashRegisterItemListNewCount >= import.meta.env.VITE_CASHREGISTERS_NEWCOUNTLIMIT) {
          dispatch('tools/showSnackbar', {
            message: i18n.t('превышен_лимит_на_кол_во_новых_касс_допустимое_кол-во', { limit: import.meta.env.VITE_CASHREGISTERS_NEWCOUNTLIMIT }),
          }, { root: true })
          dispatch('tools/setGlobalLoading', false, { root: true })
          return false
        }
        return true
      }

      // Дополняем inventory дополнительными свойствами
      miniAppConfig.inventory.query = {
        viewType: params.viewType || 'home',
      }

      const config = miniAppConfig[name]
      if (config) {
        // Проверяем требуется ли подписка для данного миниаппа
        const subscriptionRequiredVar = `VITE_MINIAPP_${name.toUpperCase()}_SUBSCRIPTION_REQUIRED`
        const isSubscriptionRequired = import.meta.env[subscriptionRequiredVar] === 'true'

        // Требуется подписка для данного миниаппа
        if (isSubscriptionRequired) {
          const { organization } = rootState.cashRegisters.cashRegister
          const subscriptionId = `${organization.accountNumber}_${name}`

          // Проверяем подписку
          const canBeOpened = await dispatch('checkMiniAppSubscription', { subscriptionId, name, params })

          // Если подписка есть, то открываем миниапп
          if (canBeOpened) {
            await dispatch('openMiniApp', { name, config, params })
          } else {
            console.warn(`MiniApp "${name}" was not opened due to subscription requirement.`)
            dispatch('tools/setGlobalLoading', false, { root: true })
          }
        } else {
          await dispatch('openMiniApp', { name, config, params })
        }
      } else {
        console.error(`MiniApp configuration for "${name}" not found.`)
        dispatch('tools/setGlobalLoading', false, { root: true })
      }
    },

    // Открыть миниапп
    async openMiniApp({ commit, dispatch }, { name, config, params }) {
      // Дополнительные проверки
      if (config.before && !config.before()) {
        console.warn(`MiniApp "${name}" was not opened due to before condition.`)
        return
      }

      // Если нужно обновить миниапп, то скрываем его и после скрытия открываем его снова
      const { refreshMiniapp } = params
      if (refreshMiniapp === 'true') {
        dispatch('close')
        // Ждем следующий тик, чтобы DOM успел обновиться после скрытия
        await Vue.nextTick()
      }

      // Открываем миниапп
      commit('set', {
        type: 'app',
        value: {
          url: config.url,
          name: config.name,
          params: Object.entries(params).map(([key, value]) => ({
            [key]: value,
          })),
        },
      })
      commit('set', { type: 'visible', value: true })

      dispatch('tools/setGlobalLoading', false, { root: true })

      // Логируем открытие миниаппа если есть source и target
      const { source, target } = params
      if (source && target) {
        dispatch('analytics/logEvent', {
          eventName: 're_miniapp_link_opened',
          eventProperties: { source, target },
        }, { root: true })
      }
    },

    // Показ модалки предупреждения о скором окончании trial (7 дней)
    async showTrialWarningModal({ dispatch }, { name }) {
      const modalKey = `${name}_trial_7_days_left`
      if (localStorage.getItem(modalKey) === 'true') {
        return true
      }
      localStorage.setItem(modalKey, 'true')

      dispatch('analytics/logEvent', { eventName: 're_miniapp_trial_period_7free_popup_shown', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })

      const productName = getProductName(name)
      return new Promise((resolve) => {
        dispatch('confirm/showConfirm', {
          title: i18n.t('осталось_7_дней_пробного_периода'),
          icon: 'mdi-alert-circle-outline',
          text: i18n.t('всё_ещё_доступна_бесплатно', { productName }),
          resolveText: i18n.t('продолжить'),
          persistent: false,
        }, { root: true }).then(() => {
          resolve(true)
        }).catch(() => {})
      })
    },

    // Показ модалки предупреждения о завершении trial завтра
    async showTrialEndingSoonModal({ dispatch }, { name }) {
      const modalKey = `${name}_trial_1_day_left`
      if (localStorage.getItem(modalKey) === 'true') {
        return true
      }

      dispatch('analytics/logEvent', { eventName: 're_miniapp_trial_period_1free_popup_shown', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })

      localStorage.setItem(modalKey, 'true')
      return new Promise((resolve) => {
        dispatch('confirm/showConfirm', {
          title: i18n.t('пробный_период_заканчивается_завтра'),
          icon: 'mdi-alert-circle-outline',
          text: i18n.t('вы_можете_выбрать_тариф_и_подключить_подписку_позже_в_разделе_мои_подписки'),
          resolveText: i18n.t('продолжить'),
          persistent: false,
        }, { root: true }).then(() => {
          resolve(true)
        }).catch(() => {})
      })
    },

    // Показ модалки предложения trial
    async showTrialOfferModal({ dispatch }, { name }) {
      const productName = getProductName(name)

      dispatch('tools/setGlobalLoading', false, { root: true })

      dispatch('analytics/logEvent', { eventName: 're_miniapp_7free_popup_shown', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })

      return new Promise((resolve) => {
        dispatch('confirm/showConfirm', {
          title: i18n.t('дарим_7_дней_бесплатно'),
          icon: 'mdi-gift-outline',
          text: i18n.t('будет_доступна_без_ограничений', { productName }),
          resolveText: i18n.t('попробовать_бесплатно'),
          rejectText: i18n.t('позже'),
          persistent: true,
        }, { root: true }).then(() => {
          dispatch('analytics/logEvent', { eventName: 're_miniapp_7free_activate', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })
          resolve(true)
        }).catch(() => {
          dispatch('analytics/logEvent', { eventName: 're_miniapp_7free_later_clicked', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })
          resolve(false)
        })
      })
    },

    // Показ модалки успешной активации trial
    showTrialActivatedModal({ dispatch }, { name }) {
      const productName = getProductName(name)
      dispatch('confirm/showConfirm', {
        title: i18n.t('пробный_период_активирован'),
        icon: 'mdi-check-circle-outline',
        text: i18n.t('в_течение_7_дней_доступна_бесплатно', { productName }),
        resolveText: i18n.t('начать_пользоваться'),
        rejectText: i18n.t('позже'),
        persistent: false,
      }, { root: true }).then(() => {
        router.push(`/miniapp?name=${name}`)
      }).catch(() => {})
    },

    // Показ модалки завершения подписки
    showSubscriptionEndedModal({ dispatch }, { name }) {
      dispatch('analytics/logEvent', { eventName: 're_miniapp_subs_not_paid_popup_shown', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })

      const productName = getProductName(name)
      dispatch('confirm/showConfirm', {
        title: i18n.t('дополнительный_функционал'),
        icon: 'mdi-alert-circle-outline',
        text: i18n.t('станет_доступна_после_выбора_тарифа_и_оплаты_подписки', { productName }),
        rejectText: i18n.t('закрыть'),
        persistent: true,
      }, { root: true }).then(() => {}).catch(() => {
        dispatch('analytics/logEvent', { eventName: 're_miniapp_subs_not_paid_choose_plan', eventProperties: { product: name, from: router.currentRoute.name } }, { root: true })
      })
    },

    // Проверка активной подписки
    async checkActiveSubscription({ dispatch }, { subscription, name }) {
      // Если подписка активна и не trial - пропускаем
      if (subscription.status === 'active' && !subscription.code.includes('trial')) {
        return true
      }

      // Если подписка активна и trial - проверяем оставшиеся дни
      if (subscription.status === 'active' && subscription.code.includes('trial')) {
        return dispatch('handleTrialWarnings', { subscription, name })
      }

      return false
    },

    // Обработка предупреждений для trial подписки
    async handleTrialWarnings({ dispatch }, { subscription, name }) {
      const { endingAt } = subscription
      let daysLeft = null
      if (endingAt) {
        const now = new Date()
        const endDate = new Date(endingAt)
        const diffTime = endDate - now
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        daysLeft = diffDays > 0 ? diffDays : 0
      }

      // Проверяем на 7 дней
      if (daysLeft === 7) {
        return dispatch('showTrialWarningModal', { name })
      }

      // Проверяем на 1 день
      if (daysLeft <= 1) {
        return dispatch('showTrialEndingSoonModal', { name })
      }

      return true
    },

    // Обработка отсутствующей подписки
    async handleMissingSubscription({ dispatch }, { subscriptionId, name }) {
      try {
        const eligibility = await dispatch('checkSubscriptionTrialEligibility', subscriptionId)

        if (eligibility?.data?.canGetTrial) {
          return dispatch('handleTrialOffer', { subscriptionId, name })
        }

        dispatch('showSubscriptionEndedModal', { name })
        return false
      } catch (eligibilityError) {
        return false
      }
    },

    // Обработка предложения trial
    async handleTrialOffer({ dispatch }, { subscriptionId, name }) {
      const shouldActivate = await dispatch('showTrialOfferModal', { name })

      if (!shouldActivate) {
        return false
      }

      try {
        const trialResult = await dispatch('applySubscriptionTrialCoupon', subscriptionId)

        if (trialResult.data.success) {
          dispatch('showTrialActivatedModal', { name })
          return false
        }

        return false
      } catch (trialError) {
        console.error('miniapp.handleTrialOffer: error activating trial:', trialError)
        return false
      }
    },

    // Обработка отсутствия номера счета
    // Раньше без passOnboarding открывался миниапп "onboarding", но он не существует —
    // всегда предлагаем зарегистрировать кассу
    handleMissingAccountNumber({ dispatch }, { name }) {
      dispatch('confirm/showConfirm', {
        title: i18n.t('доступ_ограничен'),
        icon: 'mdi-alert-circle-outline',
        text: i18n.t('сервис_работает_при_наличии_зарегистрированной_кассы'),
        resolveText: i18n.t('зарегистрировать_кассу'),
        rejectText: i18n.t('позже'),
        persistent: false,
      }, { root: true }).then(() => {
        router.push('/miniapp?name=ofd')
        dispatch('analytics/logEvent', { eventName: 're_miniapp_cash_reg_clicked', eventProperties: { product: name, button: 'cash_reg', from: router.currentRoute.name } }, { root: true })
      }).catch(() => {
        dispatch('analytics/logEvent', { eventName: 're_miniapp_cash_reg_clicked', eventProperties: { product: name, button: 'later', from: router.currentRoute.name } }, { root: true })
      })
    },

    // Основной метод проверки подписки для миниаппов
    async checkMiniAppSubscription({ dispatch, rootState }, { subscriptionId, name, params }) {
      try {
        // Проверяем есть ли у organization поле accountNumber
        if (!rootState.cashRegisters.cashRegister.organization.accountNumber) {
          dispatch('handleMissingAccountNumber', { name, params })
          return false
        }

        // Проверяем статус подписки
        const subscription = await dispatch('fetchSubscriptionStatus', subscriptionId)

        return await dispatch('checkActiveSubscription', { subscription: subscription.data, name })
      } catch (error) {
        // Если получили ошибку 404 - подписки нет
        if (error?.response?.status === 404 && error?.response?.data?.errorCode === 'SUBSCRIPTION_NOT_FOUND') {
          return dispatch('handleMissingSubscription', { subscriptionId, name })
        }
        return false
      }
    },

    // Скрыть миниапп
    close({ commit }) {
      commit('set', { type: 'visible', value: false })
      commit('set', {
        type: 'app',
        value: {
          name: null,
          url: null,
          params: {},
        },
      })
    },

    // Установка заголовков авторизации
    async setupAuthHeaders({ dispatch }) {
      const { bearerToken, userUid } = await dispatch('auth/getBearerToken', null, { root: true })
      axios.defaults.headers.common.Authorization = `Bearer ${bearerToken}`
      axios.defaults.headers['x-user-uid'] = userUid
    },

    // Получение статуса подписки
    async fetchSubscriptionStatus({ dispatch }, subscriptionId) {
      await dispatch('setupAuthHeaders')
      return axios.get(`${import.meta.env.VITE_MINIAPP_ACCOUNT_URL}/api/v1/subscriptions/${subscriptionId}`)
    },

    // Проверка возможности получения trial подписки
    async checkSubscriptionTrialEligibility({ dispatch }, subscriptionId) {
      await dispatch('setupAuthHeaders')
      return axios.get(`${import.meta.env.VITE_MINIAPP_ACCOUNT_URL}/api/v1/subscriptions/${subscriptionId}/trial-eligibility`)
    },

    // Применение купона trial подписки
    async applySubscriptionTrialCoupon({ dispatch }, subscriptionId) {
      await dispatch('setupAuthHeaders')
      return axios.post(`${import.meta.env.VITE_MINIAPP_ACCOUNT_URL}/api/v1/subscriptions/${subscriptionId}/apply-trial-coupon`)
    },
  },
}

export default miniapp
